// src/pages/super-admin/Vendors/VendorClinics.tsx
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import CreateClinic from "@/features/clinics/pages/CreateClinic";
import EditClinic from "@/features/clinics/pages/EditClinic";
import {
  useListClinicsByVendorQuery,
  useDeleteClinicMutation,
  type Clinic,
} from "@/api/clinicApi";
import { toast } from "sonner";

interface VendorClinicsProps {
  vendorId: number;
  vendorName: string;
}

export default function VendorClinics({
  vendorId,
  vendorName,
}: VendorClinicsProps) {
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedClinic, setSelectedClinic] = useState<Clinic | null>(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"ALL" | "ACTIVE" | "INACTIVE">("ALL");

  const {
    data: clinics = [],
    isLoading,
    error,
    isError,
  } = useListClinicsByVendorQuery(vendorId);
  const [deleteClinic] = useDeleteClinicMutation();

  const filteredClinics = clinics.filter((clinic) => {
    if (statusFilter !== "ALL" && clinic.status !== statusFilter) return false;
    if (query.trim()) {
      const q = query.toLowerCase();
      const haystack = [
        clinic.clinicName,
        clinic.clinicAddress,
        clinic.city,
        clinic.state,
        clinic.zipCode
      ].join(" ").toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  const handleDelete = async (id: number) => {
    if (confirm("Are you sure you want to delete this clinic?")) {
      try {
        await deleteClinic(id).unwrap();
        toast.success("Clinic deleted successfully");
      } catch (err) {
        toast.error("Failed to delete clinic");
      }
    }
  };

  const handleEdit = (clinic: Clinic) => {
    setSelectedClinic(clinic);
    setEditOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <p className="text-gray-500">Loading clinics...</p>
      </div>
    );
  }

  if (isError || error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded">
        <p className="text-red-800 font-bold">Error loading clinics</p>
        <p className="text-red-600 text-sm mt-2">{JSON.stringify(error)}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">{vendorName}</h2>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600">+ Add Clinic</Button>
          </DialogTrigger>
          <DialogContent className="w-full max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create New Clinic for {vendorName}</DialogTitle>
            </DialogHeader>
            <CreateClinic
              vendorId={vendorId}
              onClose={() => setCreateOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Clinics Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <CardTitle>Clinics ({filteredClinics.length})</CardTitle>
            <div className="flex flex-col gap-2 md:flex-row md:items-center">
              <input
                type="text"
                placeholder="Search by clinic/city/state..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-800 md:w-64"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as "ALL" | "ACTIVE" | "INACTIVE")}
                className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-800"
              >
                <option value="ALL">All Status</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {clinics.length === 0 ? (
            <div className="text-center py-8">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5.581m0 0H9m5.581 0a2 2 0 100-4 2 2 0 000 4m0 0a2 2 0 100-4 2 2 0 000 4m-6.581 0H3m0 0a2 2 0 100-4 2 2 0 000 4m0 0a2 2 0 100-4 2 2 0 000 4"
                />
              </svg>
              <p className="text-gray-500 font-medium mb-4 mt-4">
                No clinics found
              </p>
              <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600">+ Add First Clinic</Button>
                </DialogTrigger>
              </Dialog>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left px-4 py-3 font-semibold">
                      Clinic Name
                    </th>
                    <th className="text-left px-4 py-3 font-semibold">
                      Address
                    </th>
                    <th className="text-left px-4 py-3 font-semibold">City</th>
                    <th className="text-left px-4 py-3 font-semibold">State</th>
                    <th className="text-left px-4 py-3 font-semibold">Phone</th>
                    <th className="text-center px-4 py-3 font-semibold">
                      Status
                    </th>
                    <th className="text-center px-4 py-3 font-semibold">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClinics.map((clinic: Clinic) => (
                    <tr key={clinic.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">
                        {clinic.clinicName}
                      </td>
                      <td className="px-4 py-3 text-sm">{clinic.clinicAddress}</td>
                      <td className="px-4 py-3 text-sm">{clinic.city}</td>
                      <td className="px-4 py-3 text-sm">{clinic.state}</td>
                      <td className="px-4 py-3 text-sm">
                        {clinic.contactPhone}
                      </td>
                      <td className="text-center px-4 py-3">
                        <span
                          className={`px-2 py-1 rounded text-sm font-medium inline-block ${
                            clinic.status === "ACTIVE"
                              ? "bg-green-100 text-green-800"
                              : clinic.status === "INACTIVE"
                              ? "bg-red-100 text-red-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {clinic.status}
                        </span>
                      </td>
                      <td className="text-center px-4 py-3 space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(clinic)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(clinic.id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="w-full max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Clinic</DialogTitle>
          </DialogHeader>
          {selectedClinic && (
            <EditClinic
              clinic={selectedClinic}
              onClose={() => {
                setEditOpen(false);
                setSelectedClinic(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
